/* Section layout for the printable org chart.
 *
 * A single wide tree cannot be printed: print media has no scrolling, so
 * anything past the page edge is CLIPPED and silently lost. Shrinking the whole
 * tree to fit makes a 100-job chart illegible long before it fits.
 *
 * So the print does what a paper org chart has always done: break the hierarchy
 * into one SECTION PER MANAGER. Each section is a header naming the manager,
 * followed by that manager's direct reports as a row of cards that wraps. A
 * section is therefore never wider than the page, whatever the org's shape, and
 * the whole chart fits however many pages it needs.
 *
 * Sections are emitted in breadth-first order from the roots, so a reader meets
 * a manager as a card in their own manager's section before reaching the section
 * where they are the manager. That is what makes the document navigable without
 * connector lines to trace between pages.
 */

/**
 * Flatten a node forest into printable sections.
 *
 * Only nodes that HAVE children produce a section: a leaf has no reports to
 * list, and it has already been rendered as a card inside its manager's
 * section, so emitting an empty section for it would be pure noise.
 *
 * @param {Array} roots forest from buildTree
 * @returns {Array<{ id: string, manager: object|null, reports: Array }>}
 */
export function buildPrintSections(roots) {
  const sections = [];

  // The roots themselves have no shared manager, so they need a section of
  // their own or the top of the org would never appear. `manager: null` marks
  // it, and the renderer titles it from the org rather than from a person.
  if (roots.length > 0) {
    sections.push({ id: '__roots__', manager: null, reports: roots });
  }

  // Breadth-first: every manager at depth N is introduced (as a card) before
  // their own section appears, so the document reads top-down.
  const queue = [...roots];
  while (queue.length > 0) {
    const node = queue.shift();
    if (node.children.length > 0) {
      sections.push({ id: node.id, manager: node, reports: node.children });
      queue.push(...node.children);
    }
  }

  return sections;
}

/**
 * Count the cards a section set will print, so the export can tell the reader
 * how big the document is before they commit to a print dialog.
 *
 * Counts DISTINCT nodes, not card instances: every non-root node appears once as
 * a report in its manager's section, and a manager additionally appears in the
 * section header, which is not a card.
 *
 * @param {Array} sections from buildPrintSections
 * @returns {number}
 */
export function countSectionCards(sections) {
  const seen = new Set();
  for (const s of sections) {
    for (const r of s.reports) seen.add(r.id);
  }
  return seen.size;
}
